import React from "react";
import { Link } from "react-router-dom";
import { v4 as uuidV4 } from "uuid";
import { ROUTESLIST } from "@/static/urls";
import { membershipPackages } from "@/utils/constants";
import { addToCart, clearCart } from "@/services/cart";
import history from "@/store/history";
import "./MembershipTiles.scss";

const formatMoney = (n) => (n == null ? "—" : `$${Number(n).toLocaleString()}`);

// Feature copy is written with **emphasis** for the tiles, which render it as
// markup. The cart renders features as plain text, so drop the markers there.
const stripEmphasis = (feature) => feature.replace(/\*\*(.+?)\*\*/g, "$1");

const pkgToTier = (pkg) => {
  if (pkg.id === "ample-samples-pro") return "pro";
  if (pkg.id === "ample-samples-lite") return "lite";
  return null;
};

/**
 * Shared membership tile cards. Used on the memberships page and on the checkout page.
 * @param {boolean} [checkoutMode] - When true, one tile is shown as selected and the other links to switch tier.
 * @param {'pro'|'lite'} [selectedTier] - Current tier (only used when checkoutMode is true).
 * @param {string} [sectionTitle] - Optional heading above the tiles (e.g. "Choose your membership").
 */
const MembershipTiles = ({ checkoutMode = false, selectedTier = null, sectionTitle }) => {
  const sortedPackages = [...membershipPackages].sort((a, b) => a.order - b.order);

  return (
    <section className="membership-tiles">
      {sectionTitle && (
        <h2 className="membership-tiles__title">{sectionTitle}</h2>
      )}
      <div className="membership-tiles__grid">
        {sortedPackages.map((pkg) => {
          const tier = pkgToTier(pkg);
          const isSelected = checkoutMode && tier && selectedTier === tier;
          const isFeatured = Boolean(pkg.badge);
          const ctaLabel =
            pkg.ctaLabel ||
            (pkg.id === "ample-samples-pro"
              ? "Upgrade to Pro"
              : pkg.id === "ample-samples-lite"
                ? "Get Lite membership"
                : "Continue");

          const tileMods = [
            isSelected ? "membership-tile--selected" : "",
            isFeatured ? "membership-tile--featured" : "",
            pkg.hidePrice ? "membership-tile--flexible" : "",
            pkg.hideSummary ? "membership-tile--cta-pinned" : "",
          ]
            .filter(Boolean)
            .join(" ");

          return (
            <div key={pkg.id} className={`membership-tile ${tileMods}`.trim()}>
              <div className="membership-tile__content">
                <div className="membership-tile__header">
                  <h4 className="membership-tile__name">{pkg.name}</h4>
                  {pkg.badge && (
                    <span className="membership-tile__badge">⭐ {pkg.badge}</span>
                  )}
                </div>
                {!pkg.hidePrice && (
                  <div className="membership-tile__price">{formatMoney(pkg.price)}</div>
                )}
                <p className="membership-tile__who-label">Who it&apos;s for:</p>
                <p className="membership-tile__description">{pkg.description}</p>
                <ul className="membership-tile__features">
                  {pkg.features.map((f, i) => (
                    <li
                      key={i}
                      dangerouslySetInnerHTML={{
                        __html: f.replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>"),
                      }}
                    />
                  ))}
                </ul>
                {pkg.letsTalkTitle && (
                  <>
                    <p className="membership-tile__lets-talk-title">{pkg.letsTalkTitle}</p>
                    <p className="membership-tile__lets-talk-body">{pkg.letsTalkBody}</p>
                  </>
                )}
                {pkg.note ? <p className="membership-tile__note">{pkg.note}</p> : null}
              </div>
              {!pkg.hideSummary && (
                <div className="membership-tile__summary">
                  <span className="label">Total cost</span>
                  <span className="value">{formatMoney(pkg.totalCost)}</span>
                  <span className="label">Savings</span>
                  <span className="value">{formatMoney(pkg.totalSavings)} saved</span>
                </div>
              )}
              {pkg.ctaMailto ? (
                <a className="membership-tile__cta" href={`mailto:${pkg.ctaMailto}`}>
                  {ctaLabel}
                </a>
              ) : checkoutMode && isSelected ? (
                <span className="membership-tile__cta membership-tile__cta--selected">
                  Your selection — continue below
                </span>
              ) : checkoutMode && tier ? (
                <Link
                  className="membership-tile__cta"
                  to={`${ROUTESLIST.CHECKOUT_MEMBERSHIP}?tier=${tier}`}
                >
                  Switch to {tier === "pro" ? "Pro" : "Lite"}
                </Link>
              ) : (
                <button
                  type="button"
                  className="membership-tile__cta"
                  onClick={() => {
                    addToCart({
                      id: uuidV4(),
                      name: pkg.name,
                      // The checkout cart reads plan_name/plan_description/features.
                      plan_name: pkg.name,
                      plan_description: pkg.description,
                      features: pkg.features.map(stripEmphasis),
                      price: pkg.price,
                      description: `${pkg.name} Membership`,
                      priceId: pkg.priceId,
                      slug: pkg.slug,
                      product_type: 'membership',
                    });
                    history.push(ROUTESLIST.CHECKOUT);
                  }}
                >
                  {ctaLabel}
                </button>
              )}
            </div>
          );
        })}
      </div>
    </section>
  );
};

export default MembershipTiles;
